import { useEffect, useState, useCallback } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

/**
 * useA2HS — plattformsdetektering för "Lägg till på hemskärmen".
 *
 * Porterad från pwa-handel (samma kontrakt): fångar `beforeinstallprompt`
 * (Chrome/Edge/Android) för nativ installationsprompt, identifierar
 * iOS/Safari (som saknar nativ prompt) och känner av om appen redan körs i
 * standalone-läge.
 */
export function useA2HS() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAlreadyInstalled, setIsAlreadyInstalled] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      // @ts-expect-error — iOS legacy property
      window.navigator.standalone === true;
    if (standalone) {
      setIsAlreadyInstalled(true);
      return;
    }

    const ua = navigator.userAgent;
    // iOS Safari: WebKit på iPhone/iPad, men inte Chrome på iOS (CriOS)
    setIsIOS(/iPhone|iPad|iPod/i.test(ua) && /WebKit/i.test(ua) && !/CriOS/i.test(ua));

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => setIsAlreadyInstalled(true);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'dismissed' as const };
    await deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setIsInstallable(false);
    return result;
  }, [deferredPrompt]);

  return { isInstallable, isIOS, isAlreadyInstalled, promptInstall };
}
